import { SerializableObjectSchema, SerializableSchema, z } from "@repo/utils";
import {
    AddressSchema_OLD as ERPAddressSchema,
    AddressTypeSchema,
    CustomerSchema as ERPCustomerSchema,
    ErpEntityIdSchema,
    ProductSchema,
    DeliveryTermsSchema,
    PurchaseOrderExtraDefinitionSchema,
} from "../erp/z.js";
import { AttachmentIdSchema, InboxIdSchema, MessageIdSchema, ThreadIdSchema } from "../inbox/z.js";
import { OrganizationIdSchema } from "../organization/z.js";
import {
    ContactSchema,
    DeliveryTypeSchema,
    ErpSystemSchema,
    PositionCustomFieldsSchema,
    SalesOrderDictionarySchema,
    SellingEntitySchema,
    SubmitPositionInputSchema,
    SubmitTransactionInputSchema,
    WorkflowIdSchema,
    WorkflowStatusSchema,
} from "../salesorder/z.js";
import {
    ApprovalTaskIdSchema,
    ApprovalThresholdSchema,
    AssistantDecisionsSchema,
    POProcessingStatusSchema,
    PurchaseOrderProcessingErrorSchema,
    ThreadProcessingErrorSchema,
    CaseStatusSchema,
    ReclamationFeedbackSchema,
    PurchaseOrderWorkflowFieldsSchema,
    WorkflowFailureSchema,
    MasterPriceWhitelistSchema,
    AssistantMasterPriceDecisionsSchema,
    ResolvedApproverSchema,
    PurchaseOrderApprovalRolesSchema,
} from "../assistant/z.js";
import { SalesOrderConfigSchema } from "../salesorder/config/z.js";
import { PurchaseOrderComparisonSchema } from "../assistant/helpers/po/comparison/z.js";
import { OrderConfirmationProcessingResultSchema } from "../assistant/workflows/po/z.js";
import { TransactionIdSchema } from "../salesorder/results/z.js";

export {
    ContactSchema,
    ERPAddressSchema,
    AddressTypeSchema,
    ERPCustomerSchema,
    ProductSchema,
    ErpEntityIdSchema,
    ApprovalTaskIdSchema,
    AssistantDecisionsSchema,
    CaseStatusSchema,
};

export { OrganizationIdSchema, SettingSchema, UserIdSchema } from "../organization/z.js";

export { InboxIdSchema, InboxSchema, ThreadIdSchema, ThreadSchema, ThreadMetaSchema } from "../inbox/z.js";

export { ApproverRoleSchema as ApprovalRoleSchema, ApprovalThresholdSchema } from "../assistant/z.js";

export const AddressSchema = ERPAddressSchema;

export const CustomerSchema = z.intersection(
    ERPCustomerSchema,
    z.object({
        address: z.nullable(AddressSchema),
    }),
);

export const UnauthorizedBodySchema = z.object({
    invalid: z.boolean(),
});

export const ConfigSchema = z.object({
    organizationIds: z.array(OrganizationIdSchema),
});

export const FetchCustomersQueryParamsSchema = z.object({
    q: z.optional(z.string()),
});

export const FetchAddressesQueryParamsSchema = z.object({
    q: z.optional(z.string()),
    customerId: ErpEntityIdSchema,
    type: AddressTypeSchema,
});

export const FetchProductsQueryParamsSchema = z.object({
    q: z.optional(z.string()),
});

export const FetchThreadsQueryParamsSchema = z.object({
    q: z.optional(z.string()),
    offset: z.optional(z.pipe(z.string(), z.coerce.number())),
    limit: z.optional(z.pipe(z.string(), z.coerce.number())),
});

export const MailboxViewQuerySchema = z.object({
    q: z.optional(z.string()),
    inboxId: z.optional(z.string()),
    page: z.pipe(z.string(), z.coerce.number()),
});

export const EvaluationViewSchema = z.object({
    organizations: z.array(
        z.object({
            id: OrganizationIdSchema,
            name: z.string(),
        }),
    ),
});

export const StatsViewSchema = z.object({
    inputCount: z.number(),
    verdictCount: z.number(),
});

export const LayoutViewSchema = z.object({
    organizationName: z.nullable(z.string()),
    runTitle: z.nullable(z.string()),
    runWorkflow: z.nullable(z.string()),
    stats: StatsViewSchema,
});

export const RootViewSchema = z.object({
    organizationId: z.nullable(OrganizationIdSchema),
    organizations: z.array(
        z.object({
            id: OrganizationIdSchema,
            name: z.string(),
        }),
    ),
});

export const MailboxViewSchema = z.object({
    inboxes: z.array(
        z.object({
            id: InboxIdSchema,
            email: z.string(),
            idempotencyKey: z.string(),
        }),
    ),
    threads: z.array(
        z.object({
            id: ThreadIdSchema,
            inboxId: InboxIdSchema,
            externalId: z.string(),
            versionKey: z.union([z.string(), z.number()]),
            messages: z.array(
                z.object({
                    id: MessageIdSchema,
                    subject: z.nullable(z.string()),
                    content: z.string(),
                    snippet: z.nullable(z.string()),
                    senderName: z.nullable(z.string()),
                    senderEmail: z.nullable(z.string()),
                    receivedAt: z.string(),
                    sent: z.boolean(),
                    attachments: z.array(
                        z.object({
                            id: AttachmentIdSchema,
                            filename: z.string(),
                            contentType: z.string(),
                        }),
                    ),
                }),
            ),
        }),
    ),
});

export const ApprovalTaskViewSchema = z.object({
    thread: z.object({
        id: ThreadIdSchema,
        inboxId: InboxIdSchema,
        externalId: z.string(),
        versionKey: z.union([z.string(), z.number()]),
        messages: z.array(
            z.object({
                id: MessageIdSchema,
                subject: z.nullable(z.string()),
                content: z.string(),
                snippet: z.nullable(z.string()),
                senderName: z.nullable(z.string()),
                senderEmail: z.nullable(z.string()),
                receivedAt: z.string(),
                sent: z.boolean(),
                attachments: z.array(
                    z.object({
                        id: AttachmentIdSchema,
                        filename: z.string(),
                        contentType: z.string(),
                        data: z.string(),
                    }),
                ),
            }),
        ),
    }),
});

export const AssistantMasterDataSchema = z.object({
    paymentTerms: z.array(z.object({ id: z.string(), text: z.string() })),
    approvalThresholds: z.array(ApprovalThresholdSchema),
});

const reviewParts = {
    result: OrderConfirmationProcessingResultSchema,
    resolvedApprovers: z.optional(z.array(ResolvedApproverSchema)),
    comparison: PurchaseOrderComparisonSchema,
    masterPriceWhitelist: z.optional(MasterPriceWhitelistSchema),
    decisions: z.optional(AssistantDecisionsSchema),
};

const postReviewParts = {
    result: OrderConfirmationProcessingResultSchema,
    resolvedApprovers: z.optional(z.array(ResolvedApproverSchema)),
    decisions: AssistantDecisionsSchema,
    assignedKeys: z.optional(z.array(z.string())),
    approvalTaskId: z.optional(ApprovalTaskIdSchema),
    comparison: PurchaseOrderComparisonSchema,
    masterPriceWhitelist: z.optional(MasterPriceWhitelistSchema),
    masterPriceDecisions: z.optional(AssistantMasterPriceDecisionsSchema),
};

export const AssistantViewSchema = z.intersection(
    z.object({
        threadId: ThreadIdSchema,
        inboxId: InboxIdSchema,
        traceId: z.nullish(z.string()),
    }),
    z.discriminatedUnion("status", [
        z.object({ status: z.literal("pending") }),
        z.object({ status: z.literal("error"), error: ThreadProcessingErrorSchema }),
        z.object({ status: z.literal("erp-error"), error: ThreadProcessingErrorSchema }),
        z.object({
            status: z.literal("finished"),
            orders: z.array(
                z.intersection(
                    z.object({
                        processingId: z.number(),
                        number: z.string(),
                    }),
                    z.discriminatedUnion("status", [
                        z.object({ status: z.literal("pending") }),
                        z.object({
                            status: z.literal("processing-error"),
                            error: PurchaseOrderProcessingErrorSchema,
                        }),
                        z.object({
                            status: z.literal("review"),
                            ...reviewParts,
                        }),
                        z.object({
                            status: z.literal("cancelled"),
                            ...reviewParts,
                        }),
                        z.object({
                            status: z.literal("post-processing-error"),
                            ...postReviewParts,
                            failure: WorkflowFailureSchema,
                        }),
                        z.object({
                            status: z.enum(["approving", "confirming", "confirmed", "declined"]),
                            ...postReviewParts,
                        }),
                    ]),
                ),
            ),
        }),
    ]),
);

const CustomFieldsSchema = z.nullish(z.record(z.string(), SerializableSchema));

export const SalesOrderUnitOfMeasureSchema = z.object({
    externalId: z.nullish(z.string()),
    code: z.string(),
    readableSingularDe: z.string(),
    readablePluralDe: z.string(),
    readableSingularEn: z.string(),
    readablePluralEn: z.string(),
    integerOnly: z.boolean(),
    conversionFactors: z.record(z.string(), z.number()),
});

export const SalesOrderCountrySchema = z.object({
    id: z.string(),
    iso2Code: z.string(),
    iso3Code: z.string(),
    nameDe: z.string(),
    nameEn: z.string(),
    nameFr: z.string(),
});

export const SalesOrderCurrencySchema = z.object({
    id: z.string(),
    isoCode: z.string(),
    customFields: CustomFieldsSchema,
});

export const SalesOrderShippingTermsSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    customFields: CustomFieldsSchema,
});

export const SalesOrderPaymentTermsSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    customFields: CustomFieldsSchema,
});

export const SalesOrderAddressSchema = z.object({
    id: z.string(),
    sequenceNumber: z.nullish(z.string()),
    name1: z.string(),
    postalCode: z.string(),
    city: z.string(),
    countryId: z.string(),
    isDefault: z.nullish(z.boolean()),
    name2: z.nullish(z.string()),
    name3: z.nullish(z.string()),
    name4: z.nullish(z.string()),
    name5: z.nullish(z.string()),
    street1: z.nullish(z.string()),
    houseNumber1: z.nullish(z.string()),
    streetWithNumber1: z.nullish(z.string()),
    street2: z.nullish(z.string()),
    houseNumber2: z.nullish(z.string()),
    streetWithNumber2: z.nullish(z.string()),
    street3: z.nullish(z.string()),
    houseNumber3: z.nullish(z.string()),
    streetWithNumber3: z.nullish(z.string()),
    street4: z.nullish(z.string()),
    houseNumber4: z.nullish(z.string()),
    streetWithNumber4: z.nullish(z.string()),
    street5: z.nullish(z.string()),
    houseNumber5: z.nullish(z.string()),
    streetWithNumber5: z.nullish(z.string()),
    deliveryInstructions: z.nullish(z.array(z.string())),
    phone1: z.nullish(z.string()),
    phone2: z.nullish(z.string()),
    phone3: z.nullish(z.string()),
    fax1: z.nullish(z.string()),
    fax2: z.nullish(z.string()),
    fax3: z.nullish(z.string()),
    email1: z.nullish(z.string()),
    email2: z.nullish(z.string()),
    email3: z.nullish(z.string()),
    postBox: z.nullish(z.string()),
    county: z.nullish(z.string()),
    customFields: CustomFieldsSchema,
});

export const SalesOrderExtractedAddressSchema = z.object({
    name1: z.string(),
    name2: z.nullish(z.string()),
    name3: z.nullish(z.string()),
    street1: z.string(),
    houseNumber1: z.string(),
    streetWithNumber1: z.string(),
    street2: z.nullish(z.string()),
    houseNumber2: z.nullish(z.string()),
    streetWithNumber2: z.nullish(z.string()),
    street3: z.nullish(z.string()),
    houseNumber3: z.nullish(z.string()),
    streetWithNumber3: z.nullish(z.string()),
    deliveryInstructions: z.nullish(z.array(z.string())),
    postalCode: z.string(),
    postBox: z.nullish(z.string()),
    city: z.string(),
    county: z.nullish(z.string()),
    countryCode: z.string(),
});

export const SalesOrderPartnerSchema = z.object({
    id: z.string(),
    name1: z.nullish(z.string()),
    name2: z.nullish(z.string()),
    name3: z.nullish(z.string()),
    partnerNumber: z.nullish(z.string()),
    partnerSlug: z.nullish(z.string()),
    taxNumber: z.nullish(z.string()),
    domains: z.nullish(z.array(z.string())),
    billingAddresses: z.array(SalesOrderAddressSchema),
    deliveryAddresses: z.array(SalesOrderAddressSchema),
    customFields: CustomFieldsSchema,
});

export const SalesOrderCustomerAccountSchema = z.object({
    id: z.string(),
    partnerId: z.string(),
    partner: z.nullish(SalesOrderPartnerSchema),
    unionMemberId: z.nullish(z.string()),
    defaultDeliveryTerms: z.nullish(z.string()),
    defaultShippingTerms: z.nullish(z.string()),
    defaultPaymentTerms: z.nullish(z.string()),
    defaultCustomTerms1: z.nullish(z.string()),
    defaultCustomTerms2: z.nullish(z.string()),
    defaultCustomTerms3: z.nullish(z.string()),
    defaultBillingPartnerId: z.nullish(z.string()),
    defaultBillingPartner: z.nullish(SalesOrderPartnerSchema),
    defaultShippingPartnerId: z.nullish(z.string()),
    defaultShippingPartner: z.nullish(SalesOrderPartnerSchema),
    defaultPayingPartnerId: z.nullish(z.string()),
    defaultPayingPartner: z.nullish(SalesOrderPartnerSchema),
    creditLimit: z.nullish(z.number()),
    popupText: z.nullish(z.string()),
    customFields: CustomFieldsSchema,
});

const ReplacementProductSchema = z.object({
    id: z.string(),
    code: z.string(),
    title: z.string(),
});

export const SalesOrderProductSchema = z.object({
    id: z.string(),
    parentId: z.nullish(z.string()),
    code: z.string(),
    replacesProductId: z.nullish(z.string()),
    replacesProduct: z.nullish(ReplacementProductSchema),
    replacedByProductId: z.nullish(z.string()),
    replacedByProduct: z.nullish(ReplacementProductSchema),
    ean: z.nullish(z.string()),
    weight: z.nullish(z.number()),
    weightUnit: z.nullish(z.string()),
    groupId1: z.nullish(z.string()),
    groupId2: z.nullish(z.string()),
    groupId3: z.nullish(z.string()),
    groupId4: z.nullish(z.string()),
    groupId5: z.nullish(z.string()),
    groupName1: z.nullish(z.string()),
    groupName2: z.nullish(z.string()),
    groupName3: z.nullish(z.string()),
    groupName4: z.nullish(z.string()),
    groupName5: z.nullish(z.string()),
    referenceProductId1: z.nullish(z.string()),
    referenceProductId2: z.nullish(z.string()),
    referenceProductId3: z.nullish(z.string()),
    titleDe1: z.string(),
    textDe1: z.nullish(z.string()),
    textDe2: z.nullish(z.string()),
    textDe3: z.nullish(z.string()),
    textDe4: z.nullish(z.string()),
    textDe5: z.nullish(z.string()),
    titleEn1: z.string(),
    textEn1: z.nullish(z.string()),
    textEn2: z.nullish(z.string()),
    textEn3: z.nullish(z.string()),
    textEn4: z.nullish(z.string()),
    textEn5: z.nullish(z.string()),
    titleFr1: z.string(),
    textFr1: z.nullish(z.string()),
    textFr2: z.nullish(z.string()),
    textFr3: z.nullish(z.string()),
    textFr4: z.nullish(z.string()),
    textFr5: z.nullish(z.string()),
    defaultSalesUnitCode: z.nullish(z.string()),
    defaultPriceUnitCode: z.string(),
    articleNumber1: z.nullish(z.string()),
    articleNumber2: z.nullish(z.string()),
    articleNumber3: z.nullish(z.string()),
    articleNumber4: z.nullish(z.string()),
    colorId: z.nullish(z.string()),
    color: z.nullish(z.string()),
    length: z.nullish(z.number()),
    lengthUnitId: z.nullish(z.string()),
    lengthUnit: z.nullish(z.string()),
    width: z.nullish(z.number()),
    widthUnitId: z.nullish(z.string()),
    widthUnit: z.nullish(z.string()),
    height: z.nullish(z.number()),
    heightUnitId: z.nullish(z.string()),
    heightUnit: z.nullish(z.string()),
    diameter: z.nullish(z.number()),
    diameterUnitId: z.nullish(z.string()),
    diameterUnit: z.nullish(z.string()),
    sizeId: z.nullish(z.string()),
    size: z.nullish(z.string()),
    materialName1: z.nullish(z.string()),
    materialName2: z.nullish(z.string()),
    materialName3: z.nullish(z.string()),
    unitsOfMeasure: z.array(SalesOrderUnitOfMeasureSchema),
    customerArticleNumbers: z.nullish(
        z.record(
            z.string(),
            z.array(
                z.object({
                    articleNumber: z.string(),
                    customFields: z.nullish(SerializableObjectSchema),
                }),
            ),
        ),
    ),
    customFields: CustomFieldsSchema,
    blocked: z.nullish(z.boolean()),
});

export const SalesOrderCustomerConfigSchema = z.object({
    orgId: z.string(),
    config: SalesOrderConfigSchema,
    name: z.string(),
    slug: z.string(),
    domain: z.string(),
    erpSystem: ErpSystemSchema,
    syncEnabled: z.boolean(),
    legalEntityNames: z.array(z.string()),
    defaultCurrencyCode: z.string(),
    defaultLocale: z.string(),
});

export const SalesOrderMasterDataSchema = z.object({
    countries: z.array(SalesOrderCountrySchema),
    currencies: z.array(SalesOrderCurrencySchema),
    unitsOfMeasure: z.array(SalesOrderUnitOfMeasureSchema),
    shippingTerms: z.array(SalesOrderShippingTermsSchema),
    paymentTerms: z.array(SalesOrderPaymentTermsSchema),
    sellingEntities: z.array(
        z.object({
            sellingEntity: SellingEntitySchema,
            addresses: z.array(SalesOrderAddressSchema),
            defaultAddressId: z.nullable(z.string()),
        }),
    ),
    dictionaries: z.array(SalesOrderDictionarySchema),
});

export const SalesOrderPositionMatchSchema = z.object({
    id: z.string(),
    product: z.nullable(SalesOrderProductSchema),
    quantity: z.number(),
    validQuantity: z.number(),
    quantityUnitId: z.string(),
    quantityUnitCode: z.string(),
    price: z.nullish(z.number()),
    priceUnitId: z.nullish(z.string()),
    priceUnitCode: z.nullish(z.string()),
    priceQuantity: z.nullish(z.number()),
    matchingWarning: z.boolean(),
    matchingExplanation: z.nullish(z.string()),
    customFields: CustomFieldsSchema,
});

export const SalesOrderPositionSchema = z.object({
    id: z.string(),
    positionIndex: z.number(),
    extractedPositionIndex: z.nullish(z.string()),
    deliveryDate: z.nullish(z.string()),
    extractedDeliveryDate: z.nullish(z.string()),
    extractedDescription: z.string(),
    extractedQuantity1: z.number(),
    extractedQuantityUnit1: z.string(),
    extractedQuantity2: z.nullish(z.number()),
    extractedQuantityUnit2: z.nullish(z.string()),
    extractedQuantity3: z.nullish(z.number()),
    extractedQuantityUnit3: z.nullish(z.string()),
    extractedPrice1: z.nullish(z.number()),
    extractedPriceUnit1: z.nullish(z.string()),
    extractedPriceQuantity1: z.nullish(z.number()),
    extractedPrice2: z.nullish(z.number()),
    extractedPriceUnit2: z.nullish(z.string()),
    extractedPriceQuantity2: z.nullish(z.number()),
    extractedPrice3: z.nullish(z.number()),
    extractedPriceUnit3: z.nullish(z.string()),
    extractedPriceQuantity3: z.nullish(z.number()),
    extractedTotalValue: z.nullish(z.number()),
    matches: z.array(SalesOrderPositionMatchSchema),
    customFields: PositionCustomFieldsSchema,
});

export const SalesOrderSubmissionInfoSchema = z.object({
    submittedTransactionId: z.string(),
    erpTransactionId: z.string(),
    erpTransactionNumber: z.string(),
    createdAt: z.string(),
    createdBy: z.string(),
    autoSubmitted: z.boolean(),
});

export const SalesOrderAutoSubmitFailureSchema = z.object({
    failedAt: z.string(),
});

export const SalesOrderCancellationInfoSchema = z.object({
    cancelledBy: z.string(),
    cancelledAt: z.string(),
    cancelReason: z.nullable(z.string()),
});

export const SalesOrderTransactionSchema = z.object({
    id: TransactionIdSchema,
    relevantMessageIds: z.array(z.string()),
    relevantAttachmentIds: z.array(z.string()),
    customerAccounts: z.array(SalesOrderCustomerAccountSchema),
    billingAddressId: z.nullable(z.string()),
    deliveryAddressId: z.nullable(z.string()),
    sellingEntityId: z.nullish(z.string()),
    sellingEntityAddressId: z.nullish(z.string()),
    newBilledPartyAddress: z.nullish(SalesOrderExtractedAddressSchema),
    newPayingPartyAddress: z.nullish(SalesOrderExtractedAddressSchema),
    newShipToAddress: z.nullish(SalesOrderExtractedAddressSchema),
    deliveryInstructions: z.nullish(z.array(z.string())),
    receivedDate: z.string(),
    emailReceivedDate: z.nullish(z.string()),
    entryDate: z.nullish(z.string()),
    orderDate: z.string(),
    deliveryType: z.nullish(DeliveryTypeSchema),
    contactId: z.nullish(z.string()),
    contact: z.nullish(ContactSchema),
    contactName: z.string(),
    contactEmail: z.string(),
    contactPhone: z.nullish(z.string()),
    orderReference: z.nullish(z.string()),
    quoteNumber: z.nullish(z.string()),
    comment: z.nullish(z.string()),
    transactionType: z.string(),
    eligibleOrderSplitStrategy: z.nullish(z.string()),
    appliedOrderSplitStrategy: z.nullish(z.string()),
    currency: z.nullable(SalesOrderCurrencySchema),
    shippingTerms: z.nullable(z.string()),
    paymentTerms: z.nullable(z.string()),
    positions: z.array(SalesOrderPositionSchema),
    customFields: CustomFieldsSchema,
    submissionInfo: z.nullish(SalesOrderSubmissionInfoSchema),
    cancellationInfo: z.nullish(SalesOrderCancellationInfoSchema),
    autoSubmitFailure: z.nullish(SalesOrderAutoSubmitFailureSchema),
});

export const SalesOrderWorkflowResultSchema = z.object({
    workflowId: WorkflowIdSchema,
    status: WorkflowStatusSchema,
    startedAt: z.string(),
    completedAt: z.nullish(z.string()),
    inboxId: InboxIdSchema,
    transactions: z.array(SalesOrderTransactionSchema),
    attachments: z.object({
        processedIds: z.array(z.string()),
        unsupportedIds: z.array(z.string()),
        unsupportedNames: z.array(z.string()),
        errorIds: z.array(z.string()),
        errorNames: z.array(z.string()),
    }),
});

export const GetSalesOrderResultsInputSchema = z.object({
    externalThreadId: z.string(),
});

export const PendingFolderMoveSchema = z.object({
    triggerAt: z.number(),
});

export const SalesOrderSuccessResult = z.object({
    type: z.literal("RESULT"),
    threadId: ThreadIdSchema,
    inboxId: InboxIdSchema,
    customerConfig: SalesOrderCustomerConfigSchema,
    masterData: SalesOrderMasterDataSchema,
    workflowResults: z.array(SalesOrderWorkflowResultSchema),
    pendingFolderMove: z.nullish(PendingFolderMoveSchema),
});
export const GetSalesOrderResultsResponseSchema = z.discriminatedUnion("type", [
    SalesOrderSuccessResult,
    z.object({
        type: z.literal("NO_THREAD"),
    }),
    z.object({
        type: z.literal("NOT_STARTED"),
    }),
    z.object({
        type: z.literal("RUNNING"),
    }),
    z.object({
        type: z.literal("ERROR"),
    }),
]);

export const RefreshPricesInputSchema = z.object({
    threadId: ThreadIdSchema,
    customerAccountId: z.nullish(z.string()),
    partnerId: z.nullish(z.string()),
    currencyId: z.string(),
    currencyCode: z.string(),
    items: z.array(
        z.object({
            productId: z.string(),
            quantity: z.number(),
            quantityUnitId: z.nullish(z.string()),
            quantityUnitCode: z.string(),
        }),
    ),
});

export const SalesOrderAdditionalCostsRequestSchema = z.object({
    threadId: ThreadIdSchema,
    partnerId: z.string(),
    total: z.number(),
});

export const ErpPriceSchema = z.object({
    type: z.enum(["CUSTOMER_PRICE_LIST", "GLOBAL_PRICE_LIST", "PRODUCT_PRICE"]),
    currencyId: z.string(),
    currencyCode: z.string(),
    price: z.number(),
    priceUnitId: z.string(),
    priceUnitCode: z.string(),
    priceQuantity: z.number(),
    priceListTitleDE: z.optional(z.string()),
});

export const ErpPriceResponseItemSchema = z.object({
    productId: z.string(),
    quantity: z.nullable(z.number()),
    quantityUnitId: z.nullable(z.string()),
    prices: z.array(ErpPriceSchema),
    warnings: z.optional(z.array(z.enum(["BELOW_MOQ"]))),
});

export const RefreshPricesResponseSchema = z.array(ErpPriceResponseItemSchema);

export const SalesOrderAdditionalCostsResponseSchema = z.array(
    z.object({
        type: z.enum(["SHIPPING_COST"]),
        total: z.number(),
    }),
);

export const ThreadPipelineResponseSchema = z.object({
    pipeline: z.enum(["SO", "PO", "NONE", "THREAD_NOT_FOUND", "UNIVERSAL"]),
    pipelineName: z.nullable(z.string()),
});

export const SubmitSalesOrderCalculationsSchema = z.object({
    total: z.number(),
});

export const SubmitSalesOrderInputSchema = z.object({
    transaction: SubmitTransactionInputSchema,
    calculation: SubmitSalesOrderCalculationsSchema,
    positions: z.array(SubmitPositionInputSchema),
    mock: z.optional(z.boolean()),
    splitOrdersPerPosition: z.optional(z.boolean()),
});

export const SubmitSalesOrderResponseSchema = z.discriminatedUnion("success", [
    z.object({
        success: z.literal(true),
        result: z.object({
            id: z.string(),
            erpTransactionId: z.string(),
            erpTransactionNumber: z.string(),
        }),
        pendingFolderMove: z.nullish(PendingFolderMoveSchema),
    }),
    z.object({
        success: z.literal(false),
        error: z.string(),
        createdEntities: z.array(
            z.object({
                type: z.enum(["ORDER", "ADDRESS"]),
                id: z.string(),
                readableNumber: z.nullish(z.string()),
            }),
        ),
    }),
]);

export const SalesOrderDuplicateSchema = z.object({
    submittedTransactionId: z.string(),
    erpTransactionNumber: z.string(),
    createdAt: z.string(),
    createdBy: z.string(),
});

export const SearchCustomersResponseSchema = z.array(
    z.object({
        partner: SalesOrderPartnerSchema,
        customerAccount: SalesOrderCustomerAccountSchema,
    }),
);

export const AssistantMetaSchema = z.object({
    langSmithProjectId: z.optional(z.string()),
    masterData: z.object({
        paymentTerms: z.array(z.object({ id: z.string(), text: z.string() })),
        incoterms: z.array(z.object({ value: z.string(), meaning: z.string() })),
        deliveryTerms: z.array(DeliveryTermsSchema),
        extraTypes: z.nullable(PurchaseOrderExtraDefinitionSchema),
    }),
    email: z.string(),
    organizationId: z.string(),
    organizationName: z.string(),
    settings: z.object({
        approvalRoles: z.optional(PurchaseOrderApprovalRolesSchema),
        approvalThresholds: z.array(ApprovalThresholdSchema),
        readOnly: z.boolean(),
        reclamationFeedback: z.optional(ReclamationFeedbackSchema),
        minCurrencyPrecision: z.number(),
        minQuantityPrecision: z.number(),
        fields: PurchaseOrderWorkflowFieldsSchema,
    }),
});

export const PoWorkflowStatisticsSchema = z.object({
    from: z.string(),
    to: z.string(),
    inboxes: z.array(
        z.object({
            inboxId: InboxIdSchema,
            email: z.string(),
        }),
    ),
    threads: z.array(
        z.object({
            threadProcessingId: z.number(),
            status: z.enum(["pending", "finished", "error"]),
            date: z.string(),
            threadId: ThreadIdSchema,
            inboxId: InboxIdSchema,
        }),
    ),
    orders: z.array(
        z.object({
            threadProcessingId: z.number(),
            poProcessingId: z.number(),
            number: z.string(),
            status: POProcessingStatusSchema,
            date: z.string(),
        }),
    ),
    suggestions: z.array(
        z.object({
            poProcessingId: z.number(),
            key: z.string(),
            decisionType: z.enum(["accepted", "edited", "rejected"]),
            date: z.string(),
        }),
    ),
});

export const HybridSearchProductMappingSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("PRODUCT_ATTRIBUTE"),
        attributeName: z.string(),
    }),
    z.object({
        type: z.literal("CUSTOM_PRODUCT_FIELD"),
        jsonPath: z.string(),
    }),
]);

export const HybridSearchStrategyWeightsSchema = z.object({
    attributes: z.number(),
    fulltext: z.number(),
    semantic: z.number(),
    articleNumberExact: z.number(),
    articleNumberAlphanumericExact: z.number(),
    articleNumberPartial: z.number(),
    articleNumberAlphanumericPartial: z.number(),
    customerArticleNumberExact: z.number(),
    customerArticleNumberPartial: z.number(),
});

export const HybridSearchConfigSchema = z.object({
    articleNumberFields: z.array(z.string()),
    textFields: z.record(z.string(), z.string()),
    minimumArticleNumberFragmentLength: z.number(),
    semanticEmbedding: z.optional(z.boolean()),
    skipPostgresArticleNumberSearch: z.optional(z.boolean()),
    attributes: z.array(
        z.object({
            technicalName: z.string(),
            readableName: z.string(),
            nullable: z.boolean(),
            llmDescription: z.string(),
            llmType: z.enum(["string", "boolean", "number"]),
            llmEnum: z.nullable(
                z.discriminatedUnion("type", [
                    z.object({
                        type: z.literal("FIXED"),
                        enum: z.array(z.string()),
                    }),
                    z.object({
                        type: z.literal("DISTINCT_PRODUCT_VALUES"),
                    }),
                ]),
            ),
            productMapping: HybridSearchProductMappingSchema,
            elasticSearchMappingType: z.enum(["KEYWORD", "INTEGER", "LONG", "DOUBLE"]),
            elasticSearchCustomMapping: z.nullable(SerializableObjectSchema),
        }),
    ),
    elasticSearchIndexSettings: SerializableObjectSchema,
    searchWeights: z.object({
        searchByLLM: HybridSearchStrategyWeightsSchema,
        searchInAssistant: HybridSearchStrategyWeightsSchema,
    }),
});

export const HubspotMessagesSchema = z.object({
    results: z.pipe(
        z.pipe(
            z.array(z.looseObject({ type: z.string(), direction: z.optional(z.string()) })),
            z.transform(x => x.filter(y => y.type === "MESSAGE" && y.direction === "INCOMING")),
        ),
        z.array(
            z.object({
                type: z.literal("MESSAGE"),
                direction: z.literal("INCOMING"),
                subject: z.string(),
                createdAt: z.string(),
                senders: z.array(
                    z.object({
                        senderField: z.string(),
                        deliveryIdentifier: z.object({
                            type: z.literal("HS_EMAIL_ADDRESS"),
                            value: z.string().check(z.email()),
                        }),
                    }),
                ),
            }),
        ),
    ),
});
